import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TEAM_FACTION_NAMES, DISPLAY_TEAM_BORDER, DISPLAY_TEAM_TEXT } from '../displayTheme';

interface SheetVoteResultsProps {
  sessionId: string;
  activeTeam?: string; // For SEQUENTIAL mode - filter by team
  showAllTeams?: boolean; // Show all teams including those without winners
  onHeroClick?: (sheetId: string, teamColor: string) => void; // Callback when a hero card is clicked
}

interface CharacterWinner {
  teamColor: string;
  characterName: string;
  characterImage?: string;
  isPending?: boolean; // True if no winner yet
  sheetId?: string;
}

const TEAM_ORDER = ['BLUE', 'RED', 'GREEN', 'YELLOW'];

/**
 * Sheet Vote Results Display
 *
 * Mostra i personaggi vincitori per ogni team con le loro immagini.
 */
function SheetVoteResults({ sessionId, activeTeam, showAllTeams, onHeroClick }: SheetVoteResultsProps) {
  const { i18n } = useTranslation();
  const [winners, setWinners] = useState<CharacterWinner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWinners();
  }, [sessionId, activeTeam, showAllTeams]);

  const fetchWinners = async () => {
    try {
      // Fetch session to get numPlayers
      let currentNumPlayers = 3;
      const sessionResponse = await fetch(`/api/sessions/${sessionId}`);
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
        const session = sessionData.session || sessionData;
        currentNumPlayers = session.numPlayers || 4;
      }

      // Fetch all sheet votes for this session (including closed ones)
      const response = await fetch(`/api/sheet-votes/session/${sessionId}/all`);
      if (response.ok) {
        const data = await response.json();

        // Filter: only closed votes with a winner, get the LATEST one per team
        const latestVotesByTeam = new Map<string, any>();
        for (const vote of (data.votes || [])) {
          // In SEQUENTIAL mode with activeTeam, only show the active team
          if (activeTeam && vote.teamColor !== activeTeam) continue;

          if (vote.isActive === false && vote.finalSheetId) {
            const existing = latestVotesByTeam.get(vote.teamColor);
            if (!existing || new Date(vote.closedAt) > new Date(existing.closedAt)) {
              latestVotesByTeam.set(vote.teamColor, vote);
            }
          }
        }
        const latestVotes = Array.from(latestVotesByTeam.values());

        // For each latest vote per team, get the winner details
        const winnerPromises = latestVotes.map(async (vote: any) => {
          try {
            const sheetResponse = await fetch(`/api/sheets/${vote.finalSheetId}`);
            if (sheetResponse.ok) {
              const sheetData = await sheetResponse.json();
              return {
                teamColor: vote.teamColor,
                characterName: sheetData.sheet.name,
                characterImage: sheetData.sheet.imageUrl,
                sheetId: vote.finalSheetId,
                isPending: false,
              };
            }
          } catch (err) {
            console.error('Failed to fetch winner for vote:', vote.id, err);
          }
          return null;
        });

        let winnerResults = (await Promise.all(winnerPromises)).filter(Boolean) as CharacterWinner[];

        // Filter to only show teams that are part of this session
        const validTeams = new Set(TEAM_ORDER.slice(0, currentNumPlayers));
        winnerResults = winnerResults.filter(w => validTeams.has(w.teamColor));

        // If showAllTeams is true, add pending entries for teams without winners
        if (showAllTeams) {
          const teamsWithWinners = new Set(winnerResults.map(w => w.teamColor));
          const teamsToShow = TEAM_ORDER.slice(0, currentNumPlayers);

          for (const team of teamsToShow) {
            if (!teamsWithWinners.has(team)) {
              winnerResults.push({
                teamColor: team,
                characterName: i18n.language === 'it' ? 'Eroe da votare' : 'Hero to vote',
                isPending: true,
              });
            }
          }
        }

        // Sort by TEAM_ORDER
        winnerResults.sort((a, b) => TEAM_ORDER.indexOf(a.teamColor) - TEAM_ORDER.indexOf(b.teamColor));

        setWinners(winnerResults);
      }
    } catch (error) {
      console.error('Failed to fetch winners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300 mx-auto mb-8"></div>
          <p className="text-3xl text-parchment-200">
            {i18n.language === 'it' ? 'Caricamento eroi...' : 'Loading heroes...'}
          </p>
        </div>
      </div>
    );
  }

  if (winners.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-parchment-100 font-display mb-4">
            {i18n.language === 'it' ? 'Eroi Scelti' : 'Chosen Heroes'}
          </h1>
          <p className="text-3xl text-parchment-400">
            {i18n.language === 'it' ? 'Nessun eroe ancora selezionato' : 'No heroes selected yet'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col display-dark-bg p-8 overflow-hidden">
      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0">
        <h1 className="text-6xl font-bold text-parchment-100 font-display mb-2">
          {i18n.language === 'it' ? 'Gli Eroi Scelti' : 'The Chosen Heroes'}
        </h1>
      </div>

      {/* Winners Grid */}
      <div className={`grid ${winners.length === 1 ? 'grid-cols-1' : winners.length === 2 ? 'grid-cols-2' : winners.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-6 max-w-7xl mx-auto w-full flex-1 overflow-hidden`}>
        {winners.map((winner) => {
          const teamBorder = DISPLAY_TEAM_BORDER[winner.teamColor];
          const teamText = DISPLAY_TEAM_TEXT[winner.teamColor];

          // Pending teams get a muted appearance
          if (winner.isPending) {
            return (
              <div
                key={winner.teamColor}
                className={`display-card border-4 ${teamBorder} shadow-2xl flex flex-col overflow-hidden opacity-70`}
              >
                {/* Team Header */}
                <div className="text-center p-4 flex-shrink-0">
                  <h2 className={`text-4xl font-heading font-bold ${teamText} uppercase`}>
                    {TEAM_FACTION_NAMES[winner.teamColor]}
                  </h2>
                </div>

                {/* Pending Icon */}
                <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
                  <div className="text-center">
                    <div className="text-9xl mb-4 animate-pulse text-parchment-400">?</div>
                    <p className="text-3xl text-parchment-200 font-semibold">
                      {winner.characterName}
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          const isClickable = onHeroClick && winner.sheetId;
          return (
            <div
              key={winner.teamColor}
              onClick={() => isClickable && onHeroClick(winner.sheetId!, winner.teamColor)}
              className={`display-card border-4 ${teamBorder} shadow-2xl flex flex-col overflow-hidden ${isClickable ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''}`}
            >
              {/* Team Header - altezza fissa */}
              <div className="text-center p-3 flex-shrink-0">
                <p className={`text-2xl font-heading font-bold ${teamText} uppercase`}>
                  {TEAM_FACTION_NAMES[winner.teamColor]}
                </p>
              </div>

              {/* Character Image */}
              <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
                {winner.characterImage ? (
                  <img
                    src={winner.characterImage}
                    alt={winner.characterName}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  />
                ) : (
                  <div className="text-center">
                    <div className="text-9xl mb-4">⚔️</div>
                  </div>
                )}
              </div>

              {/* Character Name Footer */}
              <div className="text-center p-4 flex-shrink-0">
                <h2 className="text-3xl font-heading font-bold text-parchment-100 uppercase">
                  {winner.characterName}
                </h2>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SheetVoteResults;
